import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Form, Modal } from 'react-bootstrap';
import { createSelector } from '@reduxjs/toolkit';
import styled from 'styled-components';

import type { IGigComplianceRow } from '@/components/agent/gig-compliance/types';
import type { ISetAdherenceReasonRequest } from '@/api/types';

import { useAppSelector, useModal } from '@/hooks';
import { ModalFooter, Textarea } from '@/components/common';
import { formData } from '@/helpers';
import { EditReasonButton, Row, RowCell } from '@/components/agent/gig-compliance/ComplianceTable.styles';
import { selectAuthMenuType } from '@/store/selectors/authSelectors';
import { UserType } from '@/store/enums';
import { selectIsPendingAction } from '@/store/selectors/contractSelectors';
import { setAdherenceReason } from '@/store/slices/contractSlice';
import { hasPermission } from '@/helpers/permissionValidator';
import { Permission, PermissionLevel } from '@/constants/client/permission';

interface IReasonForm {
  additionalReasonTxt: string;
  reasonTxt: string;
  reasonNote: string;
  additionalReasonNote: string;
}

interface IProps {
  row: IGigComplianceRow;
  onSubmit: (
    payload: Omit<ISetAdherenceReasonRequest, 'contractId' | 'selectedWeekId'>,
    closeCallback: () => void,
  ) => void;
}

const reasonRowSelector = createSelector(
  selectAuthMenuType,
  selectIsPendingAction(setAdherenceReason.typePrefix),
  (userType, isPending) => ({ userType, isPending }),
);

const ReasonRow = ({ row, onSubmit }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const { userType, isPending } = useAppSelector(reasonRowSelector);

  const {
    handleSubmit,
    reset,
    control,
    register,
    formState: { errors, touchedFields },
  } = useForm<IReasonForm>({
    defaultValues: {
      reasonTxt: row?.reason?.reasonTxt ?? '',
      reasonNote: row?.reason?.reasonNote ?? '',
      additionalReasonTxt: '',
      additionalReasonNote: '',
    },
  });

  /**
   * After first render the reason is empty, so there is a need to reinitialize form
   */
  useEffect(() => {
    reset({ reasonTxt: row?.reason?.reasonTxt ?? '', reasonNote: row?.reason?.reasonNote ?? '' });
  }, [reset, row]);

  const formDataOptions = {
    register,
    errors,
    touchedFields,
  };

  const isSpUser = useMemo(() => userType === UserType.agent, [userType]);

  const parseNewText = (text: string, initValue: string) => {
    if (!!initValue) return `\n${text}`;
    return text;
  };

  const canModify = () => {
    if (userType === UserType.agent) return true;
    return hasPermission(Permission.STAFFING, PermissionLevel.MODIFY);
  };
  return (
    <Row key={row.weekSpan}>
      <RowCellReason className={row.reason?.isWarning && row.isPublished ? 'warning' : ''}>
        {row.isPublished && (
          <ReasonContainer>
            <ReasonText>
              {row.reason?.reasonTxt && t('gigCompliance.complianceTable.reasonTxt', { text: row.reason?.reasonTxt })}
            </ReasonText>
            <ReasonNote>
              {row.reason?.reasonNote &&
                t('gigCompliance.complianceTable.reasonNote', { note: row.reason?.reasonNote })}
            </ReasonNote>
          </ReasonContainer>
        )}
        {canModify() && row.isPublished && (isSpUser || (!isSpUser && row.reason.reasonTxt)) && (
          <EditReasonButton
            onClick={() =>
              openModal({
                title: t`gigCompliance.complianceTable.modalSaveReason.title`,
                size: 'm',
                content: (
                  <Form
                    onSubmit={handleSubmit(data => {
                      onSubmit(
                        {
                          additionalReasonTxt: isSpUser
                            ? parseNewText(data.additionalReasonTxt, row.reason.reasonTxt)
                            : row.reason.additionalReasonTxt,
                          weekId: row.weekId,
                          reasonNote: !isSpUser
                            ? parseNewText(data.additionalReasonNote, row.reason.reasonNote)
                            : row.reason.reasonNote,
                        },
                        closeModal,
                      );
                    })}
                  >
                    <Modal.Body>
                      <Textarea
                        control={control}
                        name="reasonTxt"
                        maxLength={200}
                        label={t(`gigCompliance.complianceTable.modalSaveReason.currentReason`)}
                        placeholder={isSpUser ? t`gigCompliance.complianceTable.modalSaveReason.placeholder` : ''}
                        disabled={true}
                        {...formData('reasonTxt', formDataOptions)}
                      />
                      <Textarea
                        control={control}
                        name="reasonNote"
                        maxLength={200}
                        label={t(`gigCompliance.complianceTable.modalSaveReason.note`)}
                        placeholder={!isSpUser ? t`gigCompliance.complianceTable.modalSaveReason.placeholder` : ''}
                        disabled={true}
                        {...formData('reasonNote', formDataOptions)}
                      />
                      {isSpUser && (
                        <Textarea
                          control={control}
                          name="additionalReasonTxt"
                          maxLength={200}
                          label={t(`gigCompliance.complianceTable.modalSaveReason.editReason`)}
                          disabled={!isSpUser}
                          {...formData('additionalReasonTxt', formDataOptions)}
                        />
                      )}
                      {!isSpUser && (
                        <Textarea
                          control={control}
                          name="additionalReasonNote"
                          maxLength={200}
                          label={t(`gigCompliance.complianceTable.modalSaveReason.editNote`)}
                          disabled={isSpUser}
                          {...formData('additionalReasonNote', formDataOptions)}
                        />
                      )}
                    </Modal.Body>
                    <ModalFooter
                      onSecondaryBtnClick={closeModal}
                      primaryDisabled={isPending}
                      secondaryDisabled={isPending}
                    />
                  </Form>
                ),
              })
            }
          />
        )}
      </RowCellReason>
    </Row>
  );
};

export { ReasonRow };

const ReasonContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const ReasonText = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin-right: 2.5rem;
  margin-left: 0.5rem;
`;

const ReasonNote = styled(ReasonText)`
  font-style: italic;
`;

const RowCellReason = styled(RowCell)`
  position: relative;
  min-height: 50px;
  height: 100%;
`;
